import { AddCircle } from "@mui/icons-material";
import { LineItemDto, MaterialDto, MaterialLineItemDto, SiteHaulingLineItemDto } from "../../../../dtos";
import { ConfirmDialog } from "../../../Core/ConfirmDialog";
import { useCallback, useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { MaterialSelect } from "../../../CommonInputs";
import { useCreateMaterialLineItemMutation } from "../../../../store/generated/generatedApi";
import { useFailedActionSnackbar, useSuccessfulActionSnackbar } from "../../../CoreLib/library";
import { BilledToType } from "../../../../dtos/enums/BilledToType";

export interface IQuickAddMaterialLineItemDialogProps {
    open: boolean;
    onClose: () => void;
    onAdd: (material: MaterialLineItemDto) => void;
    siteHaulingLineItem: LineItemDto;
    getExcludedMaterialIdsForHaulingLineItem: (haulingLineItemId: string) => string[];
    customerName?: string;
}

export const QuickAddMaterialLineItemDialog: React.FC<IQuickAddMaterialLineItemDialogProps> = ({ open, onClose, onAdd, siteHaulingLineItem, getExcludedMaterialIdsForHaulingLineItem, customerName }) => {
    const [material, setMaterial] = useState<MaterialDto>();

    const [createMaterialLineItem, { isSuccess, isError, reset }] = useCreateMaterialLineItemMutation();

    // Clear material when dialog closes
    useEffect(() => {
        if (!open) {
            setMaterial(undefined);
        }
    }, [open]);

    useSuccessfulActionSnackbar('created', 'material Line Item', isSuccess, reset);
    useFailedActionSnackbar('create', 'material Line Item', isError, reset);

    const handleOnConfirm = useCallback(() => {
        if (!material) {
            return;
        }
        const haulingLineItem = siteHaulingLineItem as SiteHaulingLineItemDto;
        const haulFromSite = haulingLineItem.site;
        const newMaterialLineItem = {
            additionalRate: undefined,
            associatedLineItemId: siteHaulingLineItem.id,
            lineItemMemo: '',
            cost: 0,
            equipmentTypeId: siteHaulingLineItem.equipmentTypeId,
            isActive: true,
            isBilledTo: BilledToType.Customer,
            billedTo: customerName ?? '',
            quantity: 1,
            materialId: material?.id,
            priceCode: 'Custom',
            quoteId: siteHaulingLineItem.quoteId,
            rate: 0,
            rateToggle: (siteHaulingLineItem as SiteHaulingLineItemDto).rateToggle,
            siteId: siteHaulingLineItem.siteId,
            unitOfMeasure: siteHaulingLineItem.unitOfMeasure,
            customerJobNumber: siteHaulingLineItem.customerJobNumber,
            poNumber: siteHaulingLineItem.poNumber,
            haulFromAddressLine1: haulingLineItem.haulFromAddressLine1 ?? haulFromSite?.addressLine1 ?? '',
            haulFromAddressLine2: haulingLineItem.haulFromAddressLine2 ?? haulFromSite?.addressLine2 ?? '',
            haulFromCity: haulingLineItem.haulFromCity ?? haulFromSite?.city ?? '',
            haulFromState: haulingLineItem.haulFromState ?? haulFromSite?.state ?? '',
            haulFromZipCode: haulingLineItem.haulFromZipCode ?? haulFromSite?.zipCode ?? '',
            haulFromCountry: haulingLineItem.haulFromCountry ?? haulFromSite?.country ?? '',
            haulFromDirections: haulingLineItem.haulFromDirections ?? haulFromSite?.directions ?? '',
            haulFromSiteName: haulingLineItem.haulFromSiteName ?? haulFromSite?.name ?? '',
            haulFromRegion: haulingLineItem.haulFromRegion ?? haulFromSite?.region,

        } as MaterialLineItemDto;

        createMaterialLineItem(newMaterialLineItem).then((response) => {
            if ((response as any).data) {
                onAdd((response as any).data);
                setMaterial(undefined);
            }
        });

    }, [createMaterialLineItem, material, onAdd, siteHaulingLineItem, customerName]);

    return (
        <ConfirmDialog
            open={open}
            onClose={onClose}
            content={
                <Grid container direction='column' spacing={2}>
                    <Grid item>
                        <Typography>Please select the Material to be hauled.</Typography>
                    </Grid>
                    <Grid item>
                        <MaterialSelect
                            selectedMaterialId={material?.id ?? ''}
                            handleSelectedMaterialChange={(selectedMaterial) => setMaterial(selectedMaterial)}
                            required
                            excludeMaterialIds={getExcludedMaterialIdsForHaulingLineItem(siteHaulingLineItem.id)}
                        />
                    </Grid>
                </Grid>
            }
            title={'Add Haul Only Material Line Item'}
            titleIcon={<AddCircle />}
            onConfirm={handleOnConfirm}
            confirmText='Confirm'
            requireConfirmation={false}
        />

    );
};
